import React, { useState } from 'react';
import { Edit2, Check, Minus, SeparatorHorizontal, SeparatorVertical } from './icons';

interface LineWidgetProps {
    orientation: 'horizontal' | 'vertical';
    style: 'solid' | 'dashed' | 'dotted';
    color: string;
    thickness: number;
    onUpdateStyle?: (updates: Partial<Pick<LineWidgetProps, 'style' | 'color' | 'thickness' | 'orientation'>>) => void;
}

const COLORS = [
    { label: 'White', value: '#e5e7eb' },
    { label: 'Gray', value: '#9ca3af' },
    { label: 'Gold', value: '#fbbf24' },
    { label: 'Red', value: '#f87171' },
    { label: 'Blue', value: '#60a5fa' },
    { label: 'Green', value: '#34d399' },
];

export const LineWidget: React.FC<LineWidgetProps> = ({
    orientation,
    style,
    color,
    thickness,
    onUpdateStyle
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const isHorizontal = orientation === 'horizontal';

    const borderStyle = style === 'solid' ? 'solid' : style === 'dashed' ? 'dashed' : 'dotted';

    const lineStyles: React.CSSProperties = isHorizontal ? {
        width: '100%',
        height: '0',
        borderTop: `${thickness}px ${borderStyle} ${color}`,
    } : {
        height: '100%',
        width: '0',
        borderLeft: `${thickness}px ${borderStyle} ${color}`,
    };

    if (isEditing && onUpdateStyle) {
        return (
            <div className="w-full h-full relative group bg-obsidian-800/50 rounded-lg border border-gold-500/30 p-2 z-40">
                <div className="absolute top-2/3 -translate-y-1/2 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-obsidian-900/95 p-1.5 rounded-full border border-gold-500/20 backdrop-blur-md shadow-xl z-50 whitespace-nowrap">
                    <div className="flex items-center gap-1 px-2 border-r border-white/10">
                        <span className="text-[10px] text-gold-500 font-bold uppercase tracking-wider">Line</span>
                    </div>

                    {/* Orientation */}
                    <div className="flex gap-0.5 bg-black/20 rounded-md p-0.5">
                        <button onClick={() => onUpdateStyle({ orientation: 'horizontal' })} className={`p-1 rounded ${orientation === 'horizontal' ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60 font-bold' : 'text-white hover:bg-white/10'}`} title="Horizontal"><SeparatorHorizontal className="w-3.5 h-3.5" /></button>
                        <button onClick={() => onUpdateStyle({ orientation: 'vertical' })} className={`p-1 rounded ${orientation === 'vertical' ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60 font-bold' : 'text-white hover:bg-white/10'}`} title="Vertical"><SeparatorVertical className="w-3.5 h-3.5" /></button>
                    </div>

                    <div className="w-px h-4 bg-white/10" />

                    {/* Style */}
                    <div className="flex gap-0.5 bg-black/20 rounded-md p-0.5">
                        <button onClick={() => onUpdateStyle({ style: 'solid' })} className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${style === 'solid' ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60 font-bold' : 'text-white hover:bg-white/10'}`}>Solid</button>
                        <button onClick={() => onUpdateStyle({ style: 'dashed' })} className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${style === 'dashed' ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60 font-bold' : 'text-white hover:bg-white/10'}`}>Dash</button>
                        <button onClick={() => onUpdateStyle({ style: 'dotted' })} className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${style === 'dotted' ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60 font-bold' : 'text-white hover:bg-white/10'}`}>Dot</button>
                    </div>

                    <div className="w-px h-4 bg-white/10" />

                    {/* Thickness */}
                    <div className="flex items-center gap-2 px-1">
                        <input
                            type="range"
                            min="1"
                            max="10"
                            value={thickness}
                            onChange={(e) => onUpdateStyle({ thickness: parseInt(e.target.value) })}
                            className="w-16 accent-gold-500 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>

                    <div className="w-px h-4 bg-white/10" />

                    {/* Color */}
                    <div className="flex gap-1 px-1">
                        {COLORS.map(c => (
                            <button
                                key={c.value}
                                onClick={() => onUpdateStyle({ color: c.value })}
                                className={`w-3 h-3 rounded-full border ${color === c.value ? 'border-white scale-125 shadow-sm' : 'border-transparent opacity-60 hover:opacity-100 hover:scale-110'} transition-all`}
                                style={{ backgroundColor: c.value }}
                                title={c.label}
                            />
                        ))}
                    </div>

                    <div className="w-px h-4 bg-white/10" />

                    <button onClick={() => setIsEditing(false)} className="p-1 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-full transition-colors" title="Done"><Check className="w-3.5 h-3.5" /></button>
                </div>

                <div className="w-full h-full flex items-center justify-center opacity-50">
                    <div style={lineStyles} />
                </div>
            </div>
        );
    }

    return (
        <div
            className="w-full h-full flex items-center justify-center relative group cursor-pointer"
            onClick={() => setIsEditing(true)}
        >
            <div style={lineStyles} />
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 p-1 rounded">
                <Edit2 className="w-3 h-3 text-gray-300" />
            </div>
        </div>
    );
};
